import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import PurchaserClient from "../PurchaserClient";
import type {
    SourcingRequestFilterParams,
    CreateSourcingRequestPayload,
    UpdateSourcingRequestPayload,
    ModifyItemsPayload,
    CreateRfqFromSourcingRequestPayload,
} from "../Purchaser/SourcingRequests";
import { queryClient, type QueryInvalidateOption } from "./client";
import { invalidateRFQListQueries } from "./rfq";

/**
 * Sourcing Request Query Options
 */
export const SourcingRequestQueryOptions = {
    SourcingRequestList(filters?: SourcingRequestFilterParams) {
        return queryOptions({
            queryKey: ["sourcingRequestList", filters],
            queryFn: async () => {
                return await PurchaserClient.getSourcingRequests(filters);
            },
            refetchOnWindowFocus: false,
            placeholderData: prev => prev,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },
    SourcingRequest(id?: number | null) {
        return queryOptions({
            queryKey: ["sourcingRequest", id],
            queryFn: async () => {
                if (!id) throw new Error("Invalid sourcing request ID");
                return await PurchaserClient.getSourcingRequest(id);
            },
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },
};

/**
 * Sourcing Request Hooks
 */
export const useSourcingRequestList = (filters?: SourcingRequestFilterParams) => {
    const query = useQuery(SourcingRequestQueryOptions.SourcingRequestList(filters));
    const { data, ...rest } = query;

    return {
        ...rest,
        requests: data?.data ?? [],
        pagination: data?.pagination,
    };
};

export const useSourcingRequest = (id?: number | null) => {
    const query = useQuery(SourcingRequestQueryOptions.SourcingRequest(id));
    const { data, refetch, ...rest } = query;

    const request = useMemo(() => data, [data]);
    const refetchRequest = useCallback(() => refetch(), [refetch]);

    return { request, refetchRequest, ...rest };
};

/**
 * Sourcing Request Invalidation Functions
 */
export async function invalidateSourcingRequestListQueries(option?: QueryInvalidateOption) {
    const queryKey = ["sourcingRequestList"];

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateSourcingRequestQueries(id?: number, option?: QueryInvalidateOption) {
    const queryKey: (string | number)[] = ["sourcingRequest"];
    if (id) queryKey.push(id);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

/**
 * Sourcing Request Mutations
 */
export const useCreateSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (payload: CreateSourcingRequestPayload) => {
            return await PurchaserClient.createSourcingRequest(payload);
        },
        async onSuccess() {
            await invalidateSourcingRequestListQueries();
        },
        onError(e) {
            console.error("Error creating sourcing request", e);
        },
    });

    return {
        createRequest: mutation.mutate,
        createRequestAsync: mutation.mutateAsync,
        isCreating: mutation.isPending,
    };
};

export const useUpdateSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (params: { id: number; payload: UpdateSourcingRequestPayload }) => {
            return await PurchaserClient.updateSourcingRequest(params.id, params.payload);
        },
        async onSuccess(_, variables) {
            await invalidateSourcingRequestListQueries();
            await invalidateSourcingRequestQueries(variables.id);
        },
        onError(e) {
            console.error("Error updating sourcing request", e);
        },
    });

    return {
        updateRequest: mutation.mutate,
        updateRequestAsync: mutation.mutateAsync,
        isUpdating: mutation.isPending,
    };
};

export const useDeleteSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (id: number) => {
            await PurchaserClient.deleteSourcingRequest(id);
            return id;
        },
        async onSuccess() {
            await invalidateSourcingRequestListQueries();
        },
        onError(e) {
            console.error("Error deleting sourcing request", e);
        },
    });

    return {
        deleteRequest: mutation.mutate,
        isDeleting: mutation.isPending,
    };
};

export const useAddItemsToSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (params: { id: number; payload: ModifyItemsPayload }) => {
            return await PurchaserClient.addItemsToSourcingRequest(params.id, params.payload);
        },
        async onSuccess(_, variables) {
            await invalidateSourcingRequestListQueries();
            await invalidateSourcingRequestQueries(variables.id);
        },
        onError(e) {
            console.error("Error adding items to sourcing request", e);
        },
    });

    return {
        addItems: mutation.mutate,
        addItemsAsync: mutation.mutateAsync,
        isAddingItems: mutation.isPending,
    };
};

export const useRemoveItemsFromSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (params: { id: number; payload: ModifyItemsPayload }) => {
            return await PurchaserClient.removeItemsFromSourcingRequest(params.id, params.payload);
        },
        async onSuccess(_, variables) {
            await invalidateSourcingRequestListQueries();
            await invalidateSourcingRequestQueries(variables.id);
        },
        onError(e) {
            console.error("Error removing items from sourcing request", e);
        },
    });

    return {
        removeItems: mutation.mutate,
        removeItemsAsync: mutation.mutateAsync,
        isRemovingItems: mutation.isPending,
    };
};

export const useCreateRfqFromSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (params: { id: number; payload: CreateRfqFromSourcingRequestPayload }) => {
            return await PurchaserClient.createRfqFromSourcingRequest(params.id, params.payload);
        },
        async onSuccess(_, variables) {
            await invalidateRFQListQueries();
            await invalidateSourcingRequestListQueries();
            await invalidateSourcingRequestQueries(variables.id);
        },
        onError(e) {
            console.error("Error creating RFQ from sourcing request", e);
        },
    });

    return {
        createRfq: mutation.mutate,
        createRfqAsync: mutation.mutateAsync,
        isCreatingRfq: mutation.isPending,
    };
};

export const useLinkRfqToSourcingRequest = () => {
    const mutation = useMutation({
        mutationFn: async (params: { sourcingRequestId: number; rfqUniquePublicId: string; catalogItemIds: number[] }) => {
            return await PurchaserClient.linkRfqToSourcingRequest(params.sourcingRequestId, params.rfqUniquePublicId, {
                catalogItemIds: params.catalogItemIds,
            });
        },
        async onSuccess(_, variables) {
            await invalidateSourcingRequestListQueries();
            await invalidateSourcingRequestQueries(variables.sourcingRequestId);
        },
        onError(e) {
            console.error("Error linking RFQ to sourcing request", e);
        },
    });

    return {
        linkRfq: mutation.mutate,
        linkRfqAsync: mutation.mutateAsync,
        isLinkingRfq: mutation.isPending,
    };
};
